import { apiClient } from './api';
import {
  CharacterStartRequest,
  CharacterStartResponse,
  CharacterIterateRequest,
  CharacterIterateResponse,
  CharacterBulkIterateRequest,
  CharacterBulkIterateResponse,
  CharacterCompactRequest,
  CharacterCompactResponse,
  CharacterSession,
  CharacterHistory,
} from '../models/character';

const SESSION_STORAGE_KEY = 'characterSessions';

export class CharacterService {
  // API calls
  static async startSession(request: CharacterStartRequest): Promise<CharacterStartResponse> {
    const response = await apiClient.post('/api/character/session/start', request);
    return response.data;
  }

  static async iterate(sessionId: string, request: CharacterIterateRequest): Promise<CharacterIterateResponse> {
    const response = await apiClient.post(`/api/character/session/${sessionId}/iterate`, request);
    return response.data;
  }

  static async bulkIterate(sessionId: string, request: CharacterBulkIterateRequest): Promise<CharacterBulkIterateResponse> {
    const response = await apiClient.post(`/api/character/session/${sessionId}/bulk-iterate`, request);
    return response.data;
  }

  static async getCompactVersion(sessionId: string, request: CharacterCompactRequest): Promise<CharacterCompactResponse> {
    const response = await apiClient.post(`/api/character/session/${sessionId}/compact`, request);
    return response.data;
  }

  // Local session management
  static createLocalSession(apiSessionId: string, concept: string, character: any, sessionName?: string): CharacterSession {
    // If character doesn't have a name but we have a sessionName, use it
    if (character && !character.name && sessionName) {
      character.name = sessionName;
    }
    
    console.log('Creating local session with character:', character);
    
    const session: CharacterSession = {
      id: `character-${Date.now()}`,
      sessionId: apiSessionId,
      initialConcept: concept,
      currentCharacter: character,
      history: [{
        character,
        timestamp: new Date().toISOString(),
      }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(id: string): CharacterSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === id) || null;
  }

  static getSessionByApiId(apiSessionId: string): CharacterSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.sessionId === apiSessionId) || null;
  }

  static getAllSessions(): CharacterSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static saveSession(session: CharacterSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(id: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== id);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static addHistory(sessionId: string, character: any, feedback?: string, changes?: string[]): void {
    const session = this.getSession(sessionId);
    if (session) {
      // Preserve the original character name if the new one doesn't have it
      if (!character.name && session.currentCharacter.name) {
        character.name = session.currentCharacter.name;
      }
      
      console.log('Adding history with character:', character);
      
      session.currentCharacter = character;
      session.history.push({
        character,
        feedback,
        changes,
        timestamp: new Date().toISOString(),
      });
      this.saveSession(session);
    }
  }

  static exportCharacter(session: CharacterSession, format: 'markdown' | 'json' = 'markdown'): string {
    if (format === 'json') {
      return JSON.stringify(session.currentCharacter, null, 2);
    }

    const char = session.currentCharacter;
    let content = `# Character Profile: ${char.name}\n\n`;
    
    // Basic Info
    content += `## Basic Information\n\n`;
    if (char.age) content += `- **Age:** ${char.age}\n`;
    if (char.gender) content += `- **Gender:** ${char.gender}\n`;
    if (char.species) content += `- **Species:** ${char.species}\n`;
    if (char.occupation) content += `- **Occupation:** ${char.occupation}\n`;
    content += '\n';

    // Appearance
    content += `## Appearance\n\n`;
    content += `${char.appearance.physicalDescription}\n\n`;
    if (char.appearance.clothingStyle) {
      content += `**Clothing Style:** ${char.appearance.clothingStyle}\n\n`;
    }
    if (char.appearance.distinguishingFeatures?.length) {
      content += `**Distinguishing Features:**\n`;
      char.appearance.distinguishingFeatures.forEach(f => content += `- ${f}\n`);
      content += '\n';
    }
    if (char.appearance.bodyLanguage) {
      content += `**Body Language:** ${char.appearance.bodyLanguage}\n\n`;
    }

    // Personality
    content += `## Personality\n\n`;
    content += `**Temperament:** ${char.personality.temperament}\n\n`;
    content += `**Traits:**\n`;
    char.personality.traits.forEach(t => content += `- ${t}\n`);
    content += '\n';
    
    if (char.personality.fears?.length) {
      content += `**Fears:**\n`;
      char.personality.fears.forEach(f => content += `- ${f}\n`);
      content += '\n';
    }
    
    if (char.personality.desires?.length) {
      content += `**Desires:**\n`;
      char.personality.desires.forEach(d => content += `- ${d}\n`);
      content += '\n';
    }
    
    if (char.personality.quirks?.length) {
      content += `**Quirks:**\n`;
      char.personality.quirks.forEach(q => content += `- ${q}\n`);
      content += '\n';
    }

    // Backstory
    content += `## Backstory\n\n`;
    content += `${char.backstory.origin}\n\n`;
    
    if (char.backstory.keyEvents?.length) {
      content += `**Key Events:**\n`;
      char.backstory.keyEvents.forEach(e => content += `- ${e}\n`);
      content += '\n';
    }
    
    if (char.backstory.trauma) {
      content += `**Trauma:** ${char.backstory.trauma}\n\n`;
    }
    
    if (char.backstory.achievements?.length) {
      content += `**Achievements:**\n`;
      char.backstory.achievements.forEach(a => content += `- ${a}\n`);
      content += '\n';
    }

    // Abilities
    if (char.abilities) {
      content += `## Abilities\n\n`;
      
      if (char.abilities.skills?.length) {
        content += `**Skills:**\n`;
        char.abilities.skills.forEach(s => content += `- ${s}\n`);
        content += '\n';
      }
      
      if (char.abilities.powers?.length) {
        content += `**Powers:**\n`;
        char.abilities.powers.forEach(p => content += `- ${p}\n`);
        content += '\n';
      }
      
      if (char.abilities.weaknesses?.length) {
        content += `**Weaknesses:**\n`;
        char.abilities.weaknesses.forEach(w => content += `- ${w}\n`);
        content += '\n';
      }
      
      if (char.abilities.fightingStyle) {
        content += `**Fighting Style:** ${char.abilities.fightingStyle}\n\n`;
      }
    }

    // Relationships
    if (char.relationships?.length) {
      content += `## Relationships\n\n`;
      char.relationships.forEach(r => {
        content += `### ${r.characterName} (${r.relationshipType})\n`;
        content += `${r.description}\n\n`;
      });
    }

    // Additional details
    if (char.motivations?.length) {
      content += `## Motivations\n\n`;
      char.motivations.forEach(m => content += `- ${m}\n`);
      content += '\n';
    }
    
    if (char.flaws?.length) {
      content += `## Flaws\n\n`;
      char.flaws.forEach(f => content += `- ${f}\n`);
      content += '\n';
    }
    
    if (char.arc) {
      content += `## Character Arc\n\n${char.arc}\n\n`;
    }

    // Session History
    content += `## Development History\n\n`;
    session.history.forEach((h, i) => {
      if (i === 0) {
        content += `### Initial Concept (${new Date(h.timestamp).toLocaleString()})\n\n`;
        content += `"${session.initialConcept}"\n\n`;
      } else {
        content += `### Iteration ${i} (${new Date(h.timestamp).toLocaleString()})\n\n`;
        if (h.feedback) content += `**Feedback:** ${h.feedback}\n\n`;
        if (h.changes?.length) {
          content += `**Changes:**\n`;
          h.changes.forEach(c => content += `- ${c}\n`);
          content += '\n';
        }
      }
    });

    return content;
  }
}